import React, { useState } from "react";
import { HiExternalLink, HiQrcode } from "react-icons/hi";
import { Copy } from "../../../atoms/Copy";
import QRCodeGenerator from "../../../atoms/QRCodeGenerator";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { FeatureBadges, FeatureLink } from "./types";

interface FeatureLinksSectionProps {
  featureLinks?: FeatureLink[];
  features?: string[];
}

const FeatureLinkRow: React.FC<{ link: FeatureLink }> = ({ link }) => {
  const [showQr, setShowQr] = useState(false);

  if (!link.publicUrl) {
    return (
      <div className="flex items-center gap-2 py-1.5">
        <StatusBadge color="gray">{link.label}</StatusBadge>
        <span className="text-xs text-gray-400">No public link</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge color="gray">{link.label}</StatusBadge>
        <div className="min-w-0 flex-1 text-xs text-gray-600">
          <Copy target={link.publicUrl} truncate longVersion />
        </div>
        <button
          type="button"
          onClick={() => setShowQr((v) => !v)}
          title={showQr ? "Hide QR code" : "Show QR code"}
          className="text-gray-500 transition-colors hover:text-blue-600"
        >
          <HiQrcode size={18} />
        </button>
        <a
          href={link.publicUrl}
          target="_blank"
          rel="noreferrer"
          title="Open public link in a new tab"
          className="text-gray-500 transition-colors hover:text-blue-600"
        >
          <HiExternalLink size={18} />
        </a>
      </div>
      {showQr && (
        <div className="flex justify-start pt-1">
          <QRCodeGenerator
            data={link.publicUrl}
            size={120}
            showLogo={false}
            ariaLabel={`QR code for ${link.label}`}
          />
        </div>
      )}
    </div>
  );
};

const FeatureLinksSection: React.FC<FeatureLinksSectionProps> = ({
  featureLinks,
  features,
}) => {
  const hasLinks = Boolean(featureLinks && featureLinks.length > 0);

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Features and public links
        </p>
      </div>

      <FeatureBadges features={features} />

      {hasLinks && (
        <div className="mt-3 flex flex-col gap-2">
          <p className="text-xs font-medium text-gray-500">Public links</p>
          {featureLinks?.map((link) => (
            <FeatureLinkRow key={link.feature} link={link} />
          ))}
        </div>
      )}
    </div>
  );
};

export default FeatureLinksSection;
